/**
 * useSiteConfig — fetches one org's site-config map once and shares it
 * via React context. Pages call useSiteConfig() to read a value with a
 * fallback when the key isn't set.
 *
 * The map is a plain Record<string, string>; values are always strings
 * (image URLs, raw text). Branding is per organization, and the provider
 * picks the org from its props:
 *   (no props)        public venue site, org from the Host header
 *   source="product"  operator domain before sign-in, RunMyTrivia brand
 *   source="org"      operator app, org of the signed-in user
 *   pin="ABCD"        projector, org that owns the live event
 * Providers nest, so the operator shell and the projector override the
 * root (Host) provider for their subtree.
 */
import { useCallback, useEffect, useState, createContext, useContext, createElement } from 'react';
import type { ReactNode } from 'react';
import api from './api';
import { PRODUCT_SITE_CONFIG } from './productBrand';

type SiteConfig = Record<string, string>;

// Facts the server computes about the org that owns this config.
export interface SiteConfigMeta {
  organizationId: number;
  orgName: string;
  // Plan feature: custom logo, custom color, and hiding the projector mark.
  customBranding: boolean;
  // Show the "Powered by RunMyTrivia" mark on the projector.
  showPoweredBy: boolean;
}

interface ContextValue {
  config: SiteConfig;
  meta: SiteConfigMeta | null;
  loaded: boolean;
  get: (key: string, fallback?: string) => string;
  // Fetch the config again, e.g. after the Branding screen saves.
  reload: () => void;
}

const Ctx = createContext<ContextValue>({
  config: {},
  meta: null,
  loaded: false,
  get: (_k, fallback = '') => fallback,
  reload: () => {},
});

// 'product' serves the static RunMyTrivia brand (utils/productBrand.ts)
// with no request. The operator domain uses it for signed-out screens.
type Source = 'host' | 'org' | 'product';

interface ProviderProps {
  children: ReactNode;
  source?: Source;
  pin?: string;
}

function endpointFor(source: Source, pin?: string): string | null {
  if (pin) return `/api/site-config/by-pin/${encodeURIComponent(pin)}`;
  if (source === 'product') return null;
  if (source === 'org') return '/api/site-config/mine';
  return '/api/site-config';
}

export const SiteConfigProvider: React.FC<ProviderProps> = ({ children, source = 'host', pin }) => {
  const endpoint = endpointFor(source, pin);
  const [config, setConfig] = useState<SiteConfig>(endpoint ? {} : PRODUCT_SITE_CONFIG);
  const [meta, setMeta] = useState<SiteConfigMeta | null>(null);
  const [loaded, setLoaded] = useState(!endpoint);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!endpoint) return;
    let mounted = true;
    api
      .get(endpoint)
      .then((res) => {
        if (!mounted) return;
        setConfig(res.data?.data ?? {});
        setMeta(res.data?.meta ?? null);
      })
      .catch(() => {
        // Pages fall back to hardcoded defaults if the request fails.
        // The request never blocks the page.
      })
      .finally(() => {
        if (mounted) setLoaded(true);
      });
    return () => { mounted = false; };
  }, [endpoint, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const get = (key: string, fallback = '') => (config[key] ?? fallback);

  // Using createElement rather than JSX so this file can stay .ts —
  // every consumer already imports from '../../utils/siteConfig'.
  return createElement(Ctx.Provider, { value: { config, meta, loaded, get, reload } }, children);
};

export function useSiteConfig() {
  return useContext(Ctx);
}

/**
 * Map an arbitrary brand hex to the nearest Radix Themes named accent.
 * Radix accents come as full 12-step palettes, so nearest-hue matching
 * themes every variant (soft/surface/outline) correctly for free —
 * exact-hex theming would need a runtime palette generator.
 */
type RadixAccent =
  | 'tomato' | 'red' | 'ruby' | 'crimson' | 'pink' | 'plum' | 'purple'
  | 'violet' | 'iris' | 'indigo' | 'blue' | 'cyan' | 'teal' | 'jade'
  | 'green' | 'grass' | 'orange' | 'amber' | 'yellow' | 'lime' | 'mint'
  | 'sky' | 'gray';

const ACCENT_HUES: Array<[RadixAccent, number]> = [
  ['red', 0], ['tomato', 12], ['orange', 28], ['amber', 42], ['yellow', 55],
  ['lime', 80], ['grass', 115], ['green', 140], ['jade', 155], ['mint', 168],
  ['teal', 178], ['cyan', 192], ['sky', 205], ['blue', 218], ['indigo', 232],
  ['iris', 242], ['violet', 255], ['purple', 272], ['plum', 292], ['pink', 322],
  ['crimson', 340], ['ruby', 350],
];

export function nearestRadixAccent(hex: string | undefined): RadixAccent {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!m) return 'ruby';
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) / 255, g = ((n >> 8) & 0xff) / 255, b = (n & 0xff) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d < 0.08) return 'gray'; // effectively unsaturated
  let hue =
    max === r ? ((g - b) / d) % 6 :
    max === g ? (b - r) / d + 2 :
    (r - g) / d + 4;
  hue = (hue * 60 + 360) % 360;
  let best: RadixAccent = 'ruby', bestDist = Infinity;
  for (const [name, h] of ACCENT_HUES) {
    const dist = Math.min(Math.abs(hue - h), 360 - Math.abs(hue - h));
    if (dist < bestDist) { bestDist = dist; best = name; }
  }
  return best;
}

// List of curated keys the admin UI exposes. Add to this when you wire
// a new editable string/image into the public site. The order here is
// the order they appear in the admin form.
export interface ConfigField {
  key: string;
  label: string;
  description?: string;
  kind: 'text' | 'textarea' | 'image' | 'color';
  // Needs the customBranding plan feature.
  gated?: boolean;
}

export const SITE_CONFIG_FIELDS: ConfigField[] = [
  // Home hero
  { key: 'homeHeroHeadline', label: 'Home — Headline', kind: 'text',
    description: 'Big text on the home page hero' },
  { key: 'homeHeroSubtext', label: 'Home — Subtext', kind: 'textarea',
    description: 'One or two sentences under the headline' },

  // About
  { key: 'aboutLead', label: 'About — Lead paragraph', kind: 'textarea' },
  { key: 'aboutBody', label: 'About — Body', kind: 'textarea',
    description: 'Markdown is not parsed — line breaks are preserved' },
  { key: 'aboutPhoto1Url', label: 'About — Photo 1', kind: 'image' },
  { key: 'aboutPhoto2Url', label: 'About — Photo 2', kind: 'image' },
  { key: 'aboutHeadshotUrl', label: 'About — Headshot', kind: 'image' },

  // Contact / Work With Us
  { key: 'contactPhone', label: 'Contact — Phone', kind: 'text' },
  { key: 'contactEmail', label: 'Contact — Email', kind: 'text' },
  { key: 'contactBlurb', label: 'Contact — Blurb', kind: 'textarea',
    description: 'Short intro on the Contact page' },
];

// Fields on the owner-only Branding screen (Settings → Branding). The
// projector, the operator shell, and the public header read these. The
// hidePoweredBy toggle is on the same screen but is not a ConfigField.
export const BRANDING_FIELDS: ConfigField[] = [
  { key: 'brandName', label: 'Brand name', kind: 'text',
    description: 'Shown in the header, the operator app, and on welcome slides' },
  { key: 'brandLogoUrl', label: 'Logo', kind: 'image', gated: true,
    description: 'Square or wide, used in the public header' },
  { key: 'brandColor', label: 'Accent color', kind: 'color', gated: true,
    description: 'Hex code, e.g. #8B3232. The app uses the closest UI accent' },
  { key: 'publicSiteUrl', label: 'Website URL', kind: 'text',
    description: 'Shown as a QR code on the presentation outro slide. Clear it to hide the QR' },
  { key: 'socialInstagramUrl', label: 'Instagram URL', kind: 'text',
    description: 'Header icon and outro QR. Clear it to hide Instagram' },
  { key: 'socialInstagramHandle', label: 'Instagram handle', kind: 'text',
    description: 'Text next to the Instagram QR, e.g. @your_handle' },
];
